import { Link, useLocation } from '@remix-run/react'
import {
	LayoutDashboard,
	Gift,
	Globe2,
	HelpCircle,
	Hexagon,
	Trophy,
	UserRound,
} from 'lucide-react'
import { cn } from '#app/utils/misc'
import logoSvg from '../images/logo.svg'

export function AppNavigation({
	selectedCount = 0,
	isAdmin = false,
}: {
	selectedCount?: number
	isAdmin?: boolean
}) {
	const { pathname } = useLocation()
	const items = [
		{
			to: '/',
			label: 'Explore',
			icon: Globe2,
			active:
				pathname === '/' ||
				pathname.startsWith('/plot/') ||
				pathname === '/claim' ||
				pathname === '/claim/saved',
		},
		{
			to: '/digital-gift',
			label: 'Gifts',
			icon: Gift,
			active: pathname === '/digital-gift' || pathname.startsWith('/gifts/'),
		},
		{
			to: '/basket',
			label: 'Basket',
			icon: Hexagon,
			active: ['/basket', '/gift-basket', '/checkout'].some(
				path => pathname === path || pathname.startsWith(path + '/'),
			),
		},
		{
			to: '/leaderboard',
			label: 'Impact',
			icon: Trophy,
			active: pathname === '/leaderboard',
		},
		{
			to: '/account',
			label: 'Account',
			icon: UserRound,
			active:
				pathname.startsWith('/account') ||
				pathname === '/login' ||
				pathname.startsWith('/profile/'),
		},
		{
			to: '/info',
			label: 'Help',
			icon: HelpCircle,
			active: [
				'/info',
				'/tree-planting',
				'/contact',
				'/terms',
				'/privacy',
				'/own-something-unique',
			].includes(pathname),
		},
	]
	if (isAdmin)
		items.push({
			to: '/admin',
			label: 'Admin',
			icon: LayoutDashboard,
			active: pathname === '/admin',
		})
	return (
		<nav aria-label="Primary navigation" className="app-navigation">
			<Link
				to="/"
				aria-label="Erda home"
				className="mb-6 hidden h-12 w-12 items-center justify-center rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 md:flex"
			>
				<img src={logoSvg} alt="Erda" className="h-10 w-auto" />
			</Link>
			{items.map(({ to, label, icon: Icon, active }) => (
				<Link
					key={to}
					to={to}
					aria-label={
						label === 'Basket' && selectedCount
							? `Basket, ${selectedCount} selected places`
							: label
					}
					aria-current={active ? 'page' : undefined}
					className={cn(
						'group relative flex min-h-12 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 md:h-12 md:w-12 md:flex-none',
						active &&
							'bg-purple-50 text-purple-800 dark:bg-purple-950 dark:text-purple-200',
					)}
				>
					<span className="relative inline-flex h-5 w-5 shrink-0 md:h-6 md:w-6">
						<Icon
							className="h-5 w-5 md:h-6 md:w-6"
							strokeWidth={active ? 2.3 : 1.7}
							aria-hidden="true"
						/>
						{label === 'Basket' && selectedCount > 0 && (
							<span
								aria-hidden="true"
								className="app-navigation-count bg-purple-600 text-white ring-2 ring-background"
							>
								{selectedCount > 99 ? '99+' : selectedCount}
							</span>
						)}
					</span>
					<span className="text-[10px] font-semibold md:hidden">{label}</span>
					<span
						aria-hidden="true"
						className="pointer-events-none absolute left-full z-50 ml-3 hidden whitespace-nowrap rounded-lg bg-foreground px-3 py-2 text-xs font-semibold text-background opacity-0 shadow-lg group-hover:opacity-100 group-focus-visible:opacity-100 md:block"
					>
						{label}
					</span>
				</Link>
			))}
		</nav>
	)
}
